import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography, TextField } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { finishJobByIdAction, getMangeDataAction } from '../../../actions/mangeActionV2';
import { showSuccessToast, showErrorToast } from '../../../slices/toastSlice';
import FileUpload from '../../../components/FileUpload';

const Row = ({ label, value, highlight }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>{label}:</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: highlight ? 'primary.main' : 'text.primary' }}>{value ?? '-'}</Typography>
  </Box>
);

export default function ConfirmJobDialog({ open, job, onClose, searchParams }) {
  const dispatch = useDispatch();
  const { finishJob } = useSelector((s) => s.manageV2) || {};
  const [requireErr, setRequireErr] = useState(false);


  const booking = job?.booking || {};
  const erd = booking?.earliestReceivingDate || '-';
  const lrd = booking?.latestReturnDate || '-';
  const containers = job?.containers || [];
  const vehiclesCount = containers.reduce((a, c) => a + (c?.shipmentItems?.length || 0), 0);
  const daysLeft = lrd ? dayjs(lrd).startOf('day').diff(dayjs().startOf('day'), 'day') : null;

  const mustUpload = job?.jobStatus === 'Loading Vehicles';
  const requiresStartDate = job?.jobStatus === 'Preparing Vehicles';
  const defaultTomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(defaultTomorrow);

  const handleSubmit = async (values) => {
    if (!job) return;
    // Enforce at least one picture per container when required
    if (mustUpload) {
      const allHavePic = (values?.containers || []).every((c) => Array.isArray(c?.pictures) && c.pictures.length > 0);
      if (!allHavePic) {
        setRequireErr(true);
        return;
      }
    }
    setRequireErr(false);
    // Build Set<Container> payload with pictures per container
    const finishContainers = containers.map((c, idx) => ({ id: c.id, pictures: values.containers?.[idx]?.pictures || [] }));
    // For Preparing Vehicles -> Loading Vehicles, require a startDate (default tomorrow)
    const startDateParam = requiresStartDate ? (startDate || defaultTomorrow) : undefined;
    // Call finish endpoint with Set<Container> in body and optional startDate param
    dispatch(finishJobByIdAction({ id: job.id, containers: finishContainers, startDate: startDateParam }));
  };

  useEffect(() => {
    if (finishJob?.loading === 'succeeded' && open) {
      dispatch(showSuccessToast('Job marked as Finished'));
      // Refresh list with current filters
      if (searchParams) {
        const query = typeof searchParams?.toString === 'function' ? searchParams.toString() : String(searchParams || '');
        dispatch(getMangeDataAction({ mode: 'job', query }));
      }
      onClose && onClose();
    } else if (finishJob?.loading === 'failed' && open) {
      dispatch(showErrorToast(finishJob.errorMessage || 'Failed to update job'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishJob?.loading]);

  if (!job) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleRoundedIcon color="success" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>Confirm job completed</Typography>
      </DialogTitle>
  <Formik enableReinitialize initialValues={{ containers: containers.map(() => ({ pictures: [] })) }} onSubmit={handleSubmit}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">Job #{job.id}</Typography>
                  <Chip size="small" label={job.jobStatus} color={job.jobStatus === 'Finished' ? 'success' : job.jobStatus === 'Canceled' ? 'error' : 'warning'} />
                </Stack>
                <Divider />
                <Row label="Client" value={job?.client ? `${job.client.name}${job.client.company ? ` (${job.client.company})` : ''}` : '-'} />
                <Row label="Booking" value={booking?.number || '-'} />
                <Row label="Vessel" value={booking?.vesselName || '-'} />
                <Row label="ERD" value={erd} />
                <Row label="LRD" value={lrd} />
                <Row label="Start Date" value={job?.startDate || '-'} />
                <Row label="Containers" value={(containers?.length || 0)} />
                <Row label="Vehicles" value={vehiclesCount} />

                <Box sx={{ p: 1, border: '1px solid #FFE0CC', bgcolor: '#FFF7F2', borderRadius: 1.5, display: 'flex', gap: 1 }}>
                  <WarningAmberRoundedIcon color="warning" fontSize="small" />
                  <Typography variant="body2">
                    Are you sure you want to mark this job as completed? This will set the status to <strong>Finished</strong>.
                  </Typography>
                </Box>

                {typeof daysLeft === 'number' && (
                  <Typography variant="caption" color={daysLeft < 0 ? 'error.main' : daysLeft <= 2 ? 'warning.main' : 'text.secondary'}>
                    Days until LRD: {daysLeft}d
                  </Typography>
                )}

                {mustUpload && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Container Pictures</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Upload at least one picture for each container.
                    </Typography>
                    <Stack spacing={1.25}>
                      {containers.map((c, idx) => (
                        <Box key={c.id || idx} sx={{ p: 1, border: '1px dashed #CBD5E1', borderRadius: 1.25 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Container {c.containerNumber || `#${idx + 1}`}
                          </Typography>
                          <FileUpload label={`Pictures for ${c.containerNumber || `Container ${idx + 1}`}`} uploadName="ContainerPictures" formikName={`containers.${idx}.pictures`} />
                        </Box>
                      ))}
                    </Stack>
                    {requireErr && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        Please upload at least one picture for every container.
                      </Typography>
                    )}
                  </Box>
                )}

                {requiresStartDate && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: .5 }}>Next Job Start Date</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      When finishing Preparing Vehicles, the system creates a Loading Vehicles job. Choose its start date (defaults to tomorrow).
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      label="Start Date"
                      InputLabelProps={{ shrink: true }}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      inputProps={{ min: dayjs().format('YYYY-MM-DD') }}
                    />
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={finishJob.loading === 'pending'}>Cancel</Button>
              <Button type="submit" variant="contained" color="success" disabled={finishJob.loading === 'pending'}>
                {finishJob.loading === 'pending' ? 'Saving…' : 'Confirm Completed'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
